/**
 * Repository — CRUD operations on the persistence layer.
 *
 * Calls go through these functions rather than touching Dexie directly,
 * so we can swap implementations later (e.g. add a remote API for sync).
 */

import type { Card, Item, Rating, ReviewSource, PlanStop } from "@music-app/learning-system";
import { isAbandonedCardId } from "@music-app/learning-system";
import {
  getDb,
  type ActivitySessionRecord,
  type ActivitySessionType,
  type CardRecord,
  type ItemEventRecord,
  type PreferenceRecord,
  type ReviewRecord,
  type SavedPlanRecord,
  type SessionRecord,
  type DrillStatsRecord,
} from "./schema.js";

export type { SavedPlanRecord } from "./schema.js";

/** Extract the curriculum node ID from an item ID (prefix before "::" separator). */
function nodeIdFromItemId(itemId: string): string {
  return itemId.split("::")[0] ?? itemId;
}

/* ─── Cards ───────────────────────────────────────────────────────────── */

export async function loadAllCards(): Promise<Record<string, Card>> {
  const db = getDb();
  const records = await db.cards.toArray();
  const out: Record<string, Card> = {};
  for (const r of records) {
    out[r.id] = r.card;
  }
  return out;
}

export async function saveCard(itemId: string, card: Card): Promise<void> {
  const db = getDb();
  await db.cards.put({
    id: itemId,
    card,
    updatedAt: new Date().toISOString(),
  });
}

export async function saveCards(cards: Record<string, Card>): Promise<void> {
  const db = getDb();
  const now = new Date().toISOString();
  const records: CardRecord[] = Object.entries(cards).map(([id, card]) => ({
    id,
    card,
    updatedAt: now,
  }));
  await db.cards.bulkPut(records);
}

/**
 * Delete abandoned orphan cards (see `isAbandonedCardId`) from local storage.
 * One-time cleanup for devices that practised pre-rehoming free-drill configs.
 * Returns the deleted ids so the caller can also prune them remotely. Idempotent.
 */
export async function pruneAbandonedCards(): Promise<string[]> {
  const db = getDb();
  const ids = (await db.cards.toArray()).map((r) => r.id).filter(isAbandonedCardId);
  if (ids.length > 0) await db.cards.bulkDelete(ids);
  return ids;
}

/* ─── Sessions ────────────────────────────────────────────────────────── */

export async function startSession(queue: Item[] = [], planStops?: PlanStop[]): Promise<string> {
  const db = getDb();
  const id = `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  await db.sessions.put({
    id,
    startedAt: new Date().toISOString(),
    endedAt: null,
    itemsReviewed: 0,
    correctCount: 0,
    averageResponseMs: 0,
    focusNodeId: null,
    queue,
    planStops,
  });
  return id;
}

/**
 * Update an in-progress session's queue + running stats. Called after every
 * review so a tab close can be resumed without losing progress.
 */
export async function updateSessionAfterReview(
  sessionId: string,
  queue: Item[],
  stats: { itemsReviewed: number; correctCount: number; averageResponseMs: number },
): Promise<void> {
  const db = getDb();
  await db.sessions.update(sessionId, { queue, ...stats });
}

export async function endSession(
  sessionId: string,
  stats: { itemsReviewed: number; correctCount: number; averageResponseMs: number },
): Promise<void> {
  const db = getDb();
  await db.sessions.update(sessionId, {
    ...stats,
    endedAt: new Date().toISOString(),
    queue: [],
  });
}

export async function getRecentSessions(limit = 30): Promise<SessionRecord[]> {
  const db = getDb();
  return db.sessions.orderBy("startedAt").reverse().limit(limit).toArray();
}

export async function getActiveSession(): Promise<SessionRecord | undefined> {
  const db = getDb();
  return db.sessions.filter((s) => s.endedAt === null).first();
}

/* ─── Reviews ─────────────────────────────────────────────────────────── */

export async function recordReview(args: {
  itemId: string;
  sessionId: string;
  rating: Rating;
  correct: boolean;
  responseMs: number;
  chosenAnswerId?: string | null;
  source?: ReviewSource;
}): Promise<void> {
  const db = getDb();
  await db.reviews.put({
    id: `review-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    ...args,
    responseMs: Math.round(args.responseMs),
  });
}

export async function getReviewsForItem(itemId: string, limit = 50): Promise<ReviewRecord[]> {
  const db = getDb();
  return db.reviews.where("itemId").equals(itemId).reverse().limit(limit).toArray();
}

/* ─── Preferences ─────────────────────────────────────────────────────── */

export async function getPreference<T>(key: string, defaultValue: T): Promise<T> {
  const db = getDb();
  const record = await db.preferences.get(key);
  if (record === undefined) return defaultValue;
  return record.value as T;
}

export async function setPreference(key: string, value: unknown): Promise<void> {
  const db = getDb();
  await db.preferences.put({ key, value, updatedAt: new Date().toISOString() });
}

/** Return all preference records (with timestamps) — used by the sync engine. */
export async function loadAllPreferences(): Promise<PreferenceRecord[]> {
  const db = getDb();
  return db.preferences.toArray();
}

/** Return raw CardRecords (with updatedAt) — used by the sync engine for LWW. */
export async function loadAllCardRecords(): Promise<CardRecord[]> {
  const db = getDb();
  return db.cards.toArray();
}

/** Return reviews with timestamp strictly after `since` (ISO string). */
export async function loadReviewsSince(since: string): Promise<ReviewRecord[]> {
  const db = getDb();
  return db.reviews.where("timestamp").above(since).toArray();
}

/** Return all review records. Used by the FSRS optimizer. */
export async function loadAllReviews(): Promise<ReviewRecord[]> {
  const db = getDb();
  return db.reviews.toArray();
}

/** Return total review count without loading all records. */
export async function countReviews(): Promise<number> {
  const db = getDb();
  return db.reviews.count();
}

/* ─── Drill stats ─────────────────────────────────────────────────────── */

export async function getDrillStats(id: string): Promise<DrillStatsRecord | undefined> {
  const db = getDb();
  return db.drillStats.get(id);
}

export async function upsertDrillStats(
  id: string,
  mode: "graded" | "listening",
  correct?: boolean,
): Promise<void> {
  const db = getDb();
  const now = new Date().toISOString();
  await db.transaction("rw", db.drillStats, async () => {
    const existing = await db.drillStats.get(id);
    if (existing) {
      await db.drillStats.update(id, {
        played: existing.played + 1,
        correct: existing.correct + (mode === "graded" && correct === true ? 1 : 0),
        errors: existing.errors + (mode === "graded" && correct === false ? 1 : 0),
        lastPlayedAt: now,
      });
    } else {
      await db.drillStats.put({
        id,
        played: 1,
        correct: mode === "graded" && correct === true ? 1 : 0,
        errors: mode === "graded" && correct === false ? 1 : 0,
        lastPlayedAt: now,
      });
    }
  });
}

/**
 * Record a melodic-dictation drill round. Stores accuracy (0–100) in
 * `accuracySum` so callers can compute rolling average accuracy.
 * Does not use the binary `correct`/`errors` fields.
 */
export async function upsertDictationDrillStats(
  id: string,
  accuracy: number, // 0–100
): Promise<void> {
  const db = getDb();
  const now = new Date().toISOString();
  await db.transaction("rw", db.drillStats, async () => {
    const existing = await db.drillStats.get(id);
    if (existing) {
      await db.drillStats.update(id, {
        played: existing.played + 1,
        accuracySum: (existing.accuracySum ?? 0) + accuracy,
        lastPlayedAt: now,
      });
    } else {
      await db.drillStats.put({
        id,
        played: 1,
        correct: 0,
        errors: 0,
        accuracySum: accuracy,
        lastPlayedAt: now,
      });
    }
  });
}

/**
 * Set of curriculum node IDs for which the user has ≥ 1 completed listen
 * session. Used by the coach plan to lift the listen-first gate once the
 * user has heard a node at least once.
 */
export async function loadListenedNodeIds(): Promise<Set<string>> {
  const db = getDb();
  const all = await db.activitySessions
    .where("sessionType").equals("listen")
    .and((s) => s.endedAt !== null && s.nodeId !== null)
    .toArray();
  const result = new Set<string>();
  for (const s of all) {
    if (s.nodeId) result.add(s.nodeId);
  }
  return result;
}

/**
 * Per-node play count for the acquisition gate.
 *
 * Counts individual item plays from item_events (listen + drill), grouped by
 * node ID derived from the item ID prefix. Bypass-quiz sessions contribute
 * their declared item_count as a floor — if the user demonstrated knowledge
 * via the quiz, they are credited with the full threshold regardless of how
 * many listen events exist.
 *
 * The _drillSkillNodes parameter is accepted but ignored; it was used by the
 * old session-aggregate approach and is retained for call-site compatibility.
 */
export async function loadNodeExposures(
  _drillSkillNodes?: Readonly<Record<string, readonly string[]>>,
): Promise<Map<string, number>> {
  const db = getDb();

  // Real play events from listen and drill sessions.
  const events = await db.itemEvents.toArray();
  const eventTotals = new Map<string, number>();
  for (const e of events) {
    eventTotals.set(e.nodeId, (eventTotals.get(e.nodeId) ?? 0) + 1);
  }

  // Bypass-quiz sessions credit the full threshold as a floor.
  const bypass = await db.activitySessions
    .where("sessionType").equals("bypass-quiz")
    .toArray();
  const bypassFloor = new Map<string, number>();
  for (const s of bypass) {
    if (s.nodeId) {
      bypassFloor.set(s.nodeId, Math.max(bypassFloor.get(s.nodeId) ?? 0, s.itemCount));
    }
  }

  // Merge: take the greater of real events and bypass floor per node.
  const allNodeIds = new Set([...eventTotals.keys(), ...bypassFloor.keys()]);
  const totals = new Map<string, number>();
  for (const nodeId of allNodeIds) {
    totals.set(nodeId, Math.max(eventTotals.get(nodeId) ?? 0, bypassFloor.get(nodeId) ?? 0));
  }
  return totals;
}

/**
 * Record that the bypass quiz was passed for a node. Writes an activity_session
 * with session_type = 'bypass-quiz' and item_count = threshold so that
 * loadNodeExposures() returns a value ≥ threshold for this node.
 */
export async function recordBypassQuizPassed(
  nodeId: string,
  threshold: number,
): Promise<void> {
  const now = new Date().toISOString();
  const id = `bypass-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const db = getDb();
  await db.activitySessions.put({
    id,
    sessionType: "bypass-quiz",
    nodeId,
    skillId: null,
    startedAt: now,
    endedAt: now,
    itemCount: threshold,
    metadata: {},
  });
}

/* ─── Activity sessions (replaces listen/drill/challenge tables) ──────── */

/** Open a new activity session. Returns the generated id. */
export async function startActivitySession(
  sessionType: ActivitySessionType,
  nodeId: string | null,
  skillId: string | null,
  metadata: Record<string, unknown> = {},
): Promise<string> {
  const db = getDb();
  const id = `${sessionType}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  await db.activitySessions.put({
    id,
    sessionType,
    nodeId,
    skillId,
    startedAt: new Date().toISOString(),
    endedAt: null,
    itemCount: 0,
    metadata,
  });
  return id;
}

/** Close an activity session with final item count and optional metadata update. */
export async function endActivitySession(
  id: string,
  itemCount: number,
  metadata?: Record<string, unknown>,
): Promise<void> {
  const db = getDb();
  await db.activitySessions.update(id, {
    endedAt: new Date().toISOString(),
    itemCount,
    ...(metadata !== undefined ? { metadata } : {}),
  });
}

/** Record a single item play within a session. Fire-and-forget safe. */
export async function recordItemEvent(
  sessionId: string,
  itemId: string,
  skillId: string | null,
  eventType: "listen" | "drill",
): Promise<void> {
  const db = getDb();
  await db.itemEvents.put({
    id: `ie-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    sessionId,
    itemId,
    nodeId: nodeIdFromItemId(itemId),
    skillId,
    eventType,
    playedAt: new Date().toISOString(),
    metadata: {},
  });
}

/** Activity sessions with startedAt strictly after `since` — used by sync and today-minutes. */
export async function loadActivitySessionsSince(since: string): Promise<ActivitySessionRecord[]> {
  const db = getDb();
  return db.activitySessions.where("startedAt").above(since).toArray();
}

/** Item events with playedAt strictly after `since` — used by sync. */
export async function loadItemEventsSince(since: string): Promise<ItemEventRecord[]> {
  const db = getDb();
  return db.itemEvents.where("playedAt").above(since).toArray();
}

/* ─── Listen coverage (drill hub) ────────────────────────────────────── */

/** Count of completed Listen sessions for each skillId. */
export async function loadAllListenCounts(): Promise<Record<string, number>> {
  const db = getDb();
  const all = await db.activitySessions
    .where("sessionType").equals("listen")
    .and((s) => s.endedAt !== null && s.skillId !== null)
    .toArray();
  const counts: Record<string, number> = {};
  for (const s of all) {
    if (s.skillId) counts[s.skillId] = (counts[s.skillId] ?? 0) + 1;
  }
  return counts;
}

/** Total item plays across Listen sessions for each skillId. */
export async function loadAllListenTotalItems(): Promise<Record<string, number>> {
  const db = getDb();
  const events = await db.itemEvents.where("eventType").equals("listen").toArray();
  const totals: Record<string, number> = {};
  for (const e of events) {
    if (e.skillId) totals[e.skillId] = (totals[e.skillId] ?? 0) + 1;
  }
  return totals;
}

/** Distinct item IDs heard in Listen mode, grouped by skillId. */
export async function loadAllListenCoverage(): Promise<Record<string, string[]>> {
  const db = getDb();
  const events = await db.itemEvents.where("eventType").equals("listen").toArray();
  const map: Record<string, Set<string>> = {};
  for (const e of events) {
    if (!e.skillId) continue;
    if (!map[e.skillId]) map[e.skillId] = new Set();
    map[e.skillId]!.add(e.itemId);
  }
  return Object.fromEntries(Object.entries(map).map(([k, v]) => [k, [...v]]));
}

/* ─── Challenge data (from activity_sessions metadata) ───────────────── */

/** Record a completed Challenge attempt. Returns the generated id. */
export async function recordChallengeAttempt(args: {
  challengeKey: string;
  startedAt: string;
  items: number;
  correct: number;
  accuracy: number;
  averageResponseMs: number;
}): Promise<string> {
  const db = getDb();
  const id = `challenge-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const endedAt = new Date().toISOString();

  // Determine personal best across existing challenge sessions for this key.
  const existing = await db.activitySessions
    .where("sessionType").equals("challenge")
    .and((s) => (s.metadata["challengeKey"] as string | undefined) === args.challengeKey)
    .toArray();
  const prevBest = existing.reduce(
    (max, s) => Math.max(max, (s.metadata["accuracy"] as number | undefined) ?? 0),
    0,
  );
  const isBest = args.accuracy >= prevBest;

  await db.activitySessions.put({
    id,
    sessionType: "challenge",
    nodeId: null,
    skillId: args.challengeKey,
    startedAt: args.startedAt,
    endedAt,
    itemCount: args.items,
    metadata: {
      challengeKey: args.challengeKey,
      correct: args.correct,
      accuracy: args.accuracy,
      averageResponseMs: args.averageResponseMs,
      best: isBest,
    },
  });
  return id;
}

/** Best accuracy + attempt count for a challengeKey. */
export async function getChallengeData(
  challengeKey: string,
): Promise<{ best: number | null; attempts: number }> {
  const db = getDb();
  const all = await db.activitySessions
    .where("sessionType").equals("challenge")
    .and((s) => s.skillId === challengeKey)
    .toArray();
  if (all.length === 0) return { best: null, attempts: 0 };
  const best = all.reduce(
    (max, s) => Math.max(max, (s.metadata["accuracy"] as number | undefined) ?? 0),
    0,
  );
  return { best, attempts: all.length };
}

/** Best accuracy + attempt count for every challengeKey in one pass. */
export async function loadAllChallengeData(): Promise<
  Record<string, { best: number | null; attempts: number }>
> {
  const db = getDb();
  const all = await db.activitySessions.where("sessionType").equals("challenge").toArray();
  const map: Record<string, { best: number; attempts: number }> = {};
  for (const s of all) {
    const key = s.skillId;
    if (!key) continue;
    const acc = (s.metadata["accuracy"] as number | undefined) ?? 0;
    const entry = map[key];
    if (!entry) {
      map[key] = { best: acc, attempts: 1 };
    } else {
      entry.best = Math.max(entry.best, acc);
      entry.attempts++;
    }
  }
  return map;
}

/* ─── Saved plans ─────────────────────────────────────────────────────── */

export async function loadUserPlans(): Promise<SavedPlanRecord[]> {
  const db = getDb();
  return db.savedPlans.where("source").equals("user").toArray();
}

export async function savePlan(plan: SavedPlanRecord): Promise<void> {
  const db = getDb();
  await db.savedPlans.put(plan);
}

export async function deletePlan(id: string): Promise<void> {
  const db = getDb();
  await db.savedPlans.delete(id);
}

export async function loadAllPlanRecords(): Promise<SavedPlanRecord[]> {
  const db = getDb();
  return db.savedPlans.toArray();
}

/* ─── Bulk ops for export/import ─────────────────────────────────────── */

export interface ExportPayload {
  version: 1;
  exportedAt: string;
  cards: CardRecord[];
  sessions: SessionRecord[];
  reviews: ReviewRecord[];
  preferences: Array<{ key: string; value: unknown }>;
  drillStats?: DrillStatsRecord[];
}

export async function exportAll(): Promise<ExportPayload> {
  const db = getDb();
  const [cards, sessions, reviews, preferences, drillStats] = await Promise.all([
    db.cards.toArray(),
    db.sessions.toArray(),
    db.reviews.toArray(),
    db.preferences.toArray(),
    db.drillStats.toArray(),
  ]);
  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    cards,
    sessions,
    reviews,
    preferences,
    drillStats,
  };
}

export async function importAll(payload: ExportPayload): Promise<void> {
  if (payload.version !== 1) {
    throw new Error(`Unsupported export version: ${payload.version}`);
  }
  const db = getDb();
  await db.transaction("rw", [db.cards, db.sessions, db.reviews, db.preferences, db.drillStats], async () => {
    await db.cards.clear();
    await db.sessions.clear();
    await db.reviews.clear();
    await db.preferences.clear();
    await db.drillStats.clear();
    await db.cards.bulkPut(payload.cards);
    await db.sessions.bulkPut(payload.sessions);
    await db.reviews.bulkPut(payload.reviews);
    await db.preferences.bulkPut(payload.preferences);
    if (payload.drillStats) {
      await db.drillStats.bulkPut(payload.drillStats);
    }
  });
}

export async function clearAll(): Promise<void> {
  const db = getDb();
  await db.transaction(
    "rw",
    [db.cards, db.sessions, db.reviews, db.preferences, db.drillStats,
     db.activitySessions, db.itemEvents],
    async () => {
      await db.cards.clear();
      await db.sessions.clear();
      await db.reviews.clear();
      await db.preferences.clear();
      await db.drillStats.clear();
      await db.activitySessions.clear();
      await db.itemEvents.clear();
    },
  );
}

/**
 * Clear progress (cards, sessions, reviews) while preserving the
 * user's preferences. Used by "Reset placement" — the user wants a
 * fresh card store but their instrument / display / leading-tone
 * settings should survive.
 */
export async function clearProgress(): Promise<void> {
  const db = getDb();
  await db.transaction("rw", [db.cards, db.sessions, db.reviews], async () => {
    await db.cards.clear();
    await db.sessions.clear();
    await db.reviews.clear();
  });
}
